import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useCustomNavigate from "../../common_nextgen/common_components/Navigation/NextGenNavigation";
import Form from "../../common_nextgen/common_components/form/Form";
import { PRICE_SCHEDULE_MODES } from "../../common_nextgen/utils/Constants";
import { useAxios } from "../../common_nextgen/axios/AxiosHelper";
import ConfirmationModal from "../../common_nextgen/common_components/Modal/ConfirmationModal";
import { useToast } from "../../common_nextgen/hooks/ToastContext";
import FunctionKeys from "../../common_nextgen/common_components/FunctionKeys/FunctionKeys";
import {
  isAddMode,
  isEditMode,
  isReadOnlyMode,
} from "../../common_nextgen/utils/UtilFunctions";
import { ROUTE_PATHS } from "../../common_nextgen/utils/Constants";

import { formFields } from "./PatientPayScheduleFormConfig";

const screenInfo = {
  ADD: ["Add Patient Pay Schedule", "RCPS3006"],
  CHANGE: ["Patient Pay Schedule Detail", "RCPS3004"],
  VIEW: ["Patient Pay Schedule Detail", "RCPS3004"],
};

const PatientPayScheduleForm = ({
  mode,
  selectMode: initialSelectMode = false,
  lookupType: initialLookupType,
  hideModal,
  initialSelectedPatient = [],
}) => {
  const { PATIENT_PAY_SCHEDULE } = ROUTE_PATHS;
  const { axios_helper_no_header } = useAxios();
  const location = useLocation();
  //const { state: stateVal } = location;
  //const stateVal = location.state || [];
  const [stateVal, setStateVal] = useState(location.state || []);
  const { currentState, previousState } = useMemo(() => {
    let [currentState = {}] = [...stateVal].reverse();
    const previousState = [...stateVal];
    previousState.splice(stateVal.length - 1, 1);
    return {
      previousState,
      currentState,
    };
  }, [stateVal]);

  const {
    selectedPatients = [], // all the selected rows
    previousScreen = PATIENT_PAY_SCHEDULE,
    currentIndex: initialIndex = 0,
    externalMode: initialExternalMode = false,
  } = useMemo(() => {
    const {
      selectedPatients = [], // all the selected rows
      previousScreen = PATIENT_PAY_SCHEDULE,
      currentIndex,
      externalMode,
    } = currentState;
    return {
      selectedPatients,
      previousScreen,
      currentIndex,
      externalMode,
    };
  }, [currentState]);
  // const { selectedPriceSchedules = [], previousScreen = '/patient-pay-schedule', currentIndex: initialIndex = 0 } = location.state || {};
  const navigateToPage = useCustomNavigate();
  const [selectedOption, setSelectedOption] = useState("no");
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentMode, setCurrentMode] = useState(mode);
  const [initialFormData, setInitialFormData] = useState({});
  const [constraintErrors, setConstraintErrors] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(initialSelectMode);
  const [lookupType, setLookupType] = useState(initialLookupType);
  const [externalMode, setExternalMode] = useState(initialExternalMode);
  const [selectedPatient, setSelectedPatient] = useState(
    initialSelectedPatient
  );
  const toast = useToast();

  const handleBack = () => {
    if (externalMode) {
      /*if (lookupType === "external") {
        const message = {
          type: "hideModal",
        };
        window.parent.postMessage(message, "*");
      } else {
        hideModal();
      }*/
      const message = {
        message: "navigate-to-external-program",
        url: previousScreen,
        parameters: {
          state: [...previousState],
        },
      };
      console.log("back messages", message);
      window.parent.postMessage(message, "*");
    } else if (currentIndex < selectedPatients.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentMode(mode);
    } else {
      const lastScheduleName =
        selectedPatients[currentIndex]?.ps3PatientPaySchName || "";
      const updatedParamsData = {
        ...currentState.paramsData,
        scheduleName: lastScheduleName,
        hiddenRecordIndicator: "Active",
      };
      const latestPreviousState = previousState.pop();
      navigateToPage(previousScreen, [
        ...previousState,
        {
          ...latestPreviousState,
          paramsData: updatedParamsData,
          currentIndex,
        },
      ]);
    }
  };

  const getPageTitle = () => {
    const [screenTitle, screenNumber] = isAddMode(mode)
      ? screenInfo.ADD
      : screenInfo.CHANGE;
    return (
      <>
        <span className="title">{screenTitle}</span>
        <span>{"-"}</span>
        <span className="screen-number">{screenNumber}</span>
      </>
    );
  };

  const submitForm = async (dataForSubmission) => {
    let url = "";
    let verb = "";
    let screenNumber = "";

    if (isAddMode(currentMode)) {
      url = "api/patientpayschedule";
      verb = "POST";
      screenNumber = screenInfo.ADD[1];
    } else {
      url = "api/patientpayschedule";
      verb = "PUT";
      screenNumber = screenInfo.CHANGE[1];

      if (
        JSON.stringify(dataForSubmission) === JSON.stringify(initialFormData)
      ) {
        toast.current.show({
          severity: "info",
          summary: "",
          detail: "No Change of Information",
          life: 3000,
        });
        return;
      }
    }

    const data = { ...dataForSubmission, screenNumber };

    try {
      const { responseStatus, responseData } = await axios_helper_no_header(
        verb,
        url,
        data,
        null
      );
      toast.current.show({
        severity: "success",
        summary: "",
        detail: responseStatus.message,
        life: 3000,
      });
      setInitialFormData(responseData);
      if (isAddMode(currentMode)) {
        setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
        const [previousLastRecord = {}] = previousState.splice(
          previousState.length - 1,
          1
        );
        const { paramsData = {} } = previousLastRecord;
        const updatedParamsData = {
          ...paramsData,
        };
        const updatedPreviousState = [
          ...previousState,
          { ...previousLastRecord, paramsData: updatedParamsData },
        ];
        const updatedCurrentState = {
          ...currentState,
          selectedPatients: [{ ...responseData }],
          currentIndex: 0,
          previousScreen: previousScreen,
        };
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_SCHEDULE_EDIT, [
          ...updatedPreviousState,
          { ...updatedCurrentState },
        ]);
        setStateVal([
          ...updatedPreviousState,
          { ...updatedCurrentState },
        ]);
      } else {
        setInitialFormData(responseData);
      }
    } catch (error) {
      const {
        responseStatus: { displayErrorFields = null, message },
      } = error.data;
      if (displayErrorFields === null) {
        toast.current.show({
          severity: "error",
          summary: "",
          detail: message,
          life: 3000,
        });
      } else {
        setConstraintErrors(displayErrorFields);
      }
    }
  };

  const [keyFunc, keyName] = useMemo(() => {
    const keyFunc = {};
    const keyName = {};
    if (isAddMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
    } else if (isEditMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.READ_ONLY);
      keyName.chmod = "View";

      keyName.F7 = "Client Patient Pay Schedules";
      keyFunc.F7 = () =>
        navigateToPage(ROUTE_PATHS.CLIENT_PATIENT_PAY_SCHEDULE, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);

      keyName.F9 = "Client Limits";
      keyFunc.F9 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_CLIENT_LIMITS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            previousScreen: location.pathname,
            currentIndex: 0,
          },
        ]);

      keyName.F14 = "Note";
      keyFunc.F14 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_NOTE, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);

      keyFunc.F15 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_SCHEDULE_CRITERIA, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);
      keyName.F15 = "Criteria";
    } else if (isReadOnlyMode(currentMode)) {
      keyFunc.F12 = handleBack;
      keyName.F12 = "Back";
      keyFunc.chmod = () => setCurrentMode(PRICE_SCHEDULE_MODES.EDIT);
      keyName.chmod = "Edit";

      keyName.F7 = "Client Patient Pay Schedules";
      keyFunc.F7 = () =>
        navigateToPage(ROUTE_PATHS.CLIENT_PATIENT_PAY_SCHEDULE, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);

      keyName.F9 = "Client Limits";
      keyFunc.F9 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_CLIENT_LIMITS, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);

      keyName.F14 = "Note";
      keyFunc.F14 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_NOTE, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);

      keyFunc.F15 = () =>
        navigateToPage(ROUTE_PATHS.PATIENT_PAY_SCHEDULE_CRITERIA, [
          ...previousState,
          { ...currentState, currentIndex, externalMode },
          {
            selectedPatients: [initialFormData],
            currentIndex: 0,
            previousScreen: location.pathname,
          },
        ]);
      keyName.F15 = "Criteria";
    }

    return [keyFunc, keyName];
  }, [currentMode, currentIndex, initialFormData, selectMode, externalMode]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const url = isAddMode(currentMode)
        ? `api/patientpayschedule/initialFormData/${encodeURIComponent(
            screenInfo.ADD[1]
          )}`
        : `api/patientpayschedule/${encodeURIComponent(
            selectMode
              ? selectedPatient[0]?.ps3PatientPaySchName
              : selectedPatients[currentIndex]?.ps3PatientPaySchName
          )}`;
      try {
        const { responseData } = await axios_helper_no_header("GET", url);
        setInitialFormData(responseData);
      } catch (e) {
        console.error(e);
      }
    };
    if (isAddMode(currentMode) || selectedPatients.length) {
      fetchInitialData();
    }
  }, [mode, currentIndex, selectMode, currentState, selectedPatients]);

  const handleMessage = (event) => {
    const { type, data } = event.data;
    console.log("iframe load", type, data);
    if (type === "initData") {
      const { selectMode, data: paramData } = data;
      console.log("external data", paramData, selectMode);
      setLookupType("external");
      //setSelectParams(paramData.params);
      setSelectedPatient([...paramData.selectedSchedule]);
      setSelectMode(selectMode);
    } else if (type === "set-params") {
      setStateVal(data);
      setExternalMode(true);
    }
    console.log("message", event.data);
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  });

  return (
    <div className={`grid container ${selectMode ? "popup" : ""}`}>
      <div className="col col-12">
        <FunctionKeys keyFunc={keyFunc} keyName={keyName} />
      </div>
      <div className="col col-12">
        <h1>{getPageTitle()}</h1>
      </div>
      <div className="col col-12">
        <Form
          formData={initialFormData}
          formFields={formFields}
          mode={currentMode}
          submitFormData={submitForm}
          constraintErrors={constraintErrors}
        />
      </div>
    </div>
  );
};

export default PatientPayScheduleForm;
